import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import type { LessonPracticeHistoryPage, LessonPracticeReview, LessonPracticeTask, PracticeReviewInput } from "@wmf/shared/practiceReview";
import { AWARD_CATALOGUE, TUTOR_RECOGNITIONS } from "@wmf/shared/studentAwards";
import { get, send } from "../api.js";
import { formatDate, formatDuration } from "../format.js";
import { addStaffPracticeTodo } from "../practiceTodos.js";
import { ConfirmDialog, Drawer } from "../ui/Dialog.js";
import { PracticeTaskMaterials } from "../PracticeTaskMaterials.js";
import { PracticeTaskGrooveChoice } from "../PracticeTaskGrooveChoice.js";
import { emptyPracticeTaskDraft, type PracticeTaskDraft } from "../practiceTaskDraft.js";
import { useAsync } from "../useAsync.js";
import "../lessonPracticeReview.css";

type Target = { domain: "private"; bookingId: string; accountId?: string | null; studentId?: string | null } | { domain: "school"; sessionId: string; pupilId: string; accountId?: string | null };
interface PracticeHistoryState {
  key: string;
  base: (LessonPracticeReview & { key: string }) | null;
  tasks: LessonPracticeTask[];
  nextCursor: string | null;
  hasMore: boolean;
  error: string | null;
  loading: boolean;
}
const emptyPracticeHistory = (key: string, base: PracticeHistoryState["base"]): PracticeHistoryState => ({
  key, base, tasks: [], nextCursor: base?.completedHistoryNextCursor ?? null,
  hasMore: base?.completedHistoryHasMore === true, error: null, loading: false,
});

export function useLessonPracticeReview(target: Target, onChange?: () => void) {
  const key = target.domain === "private"
    ? `${target.accountId ?? "anonymous"}:${target.bookingId}`
    : `${target.accountId ?? "anonymous"}:${target.sessionId}:${target.pupilId}`;
  const source = useAsync(async () => ({ key, ...await (target.domain === "private"
    ? get<LessonPracticeReview>(`/admin/bookings/${target.bookingId}/practice-review`)
    : get<LessonPracticeReview>("/school-domain/lesson-workspace/practice-review", { sessionId: target.sessionId, pupilId: target.pupilId })) }), [key, target.domain]);
  const [accepted, setAccepted] = useState<(LessonPracticeReview & { key: string }) | null>(null);
  const [changes, setChanges] = useState<Record<string, PracticeReviewInput>>({});
  const opener = useRef<HTMLButtonElement | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const data = accepted?.key === key ? accepted : source.data?.key === key ? source.data : null;
  const tasks = (data?.tasks ?? []).map(task => ({ ...task, review: changes[task.id] ?? task.review }));
  const [historyState, setHistoryState] = useState<PracticeHistoryState>(() => emptyPracticeHistory(key, null));
  const historyScope = useRef({ key, version: 0 });
  if (historyScope.current.key !== key) historyScope.current = { key, version: historyScope.current.version + 1 };
  const historyRequestSequence = useRef(0);
  const historyInFlight = useRef<{ key: string; version: number; sequence: number } | null>(null);
  const resetHistory = (base: PracticeHistoryState["base"]) => {
    historyRequestSequence.current += 1;
    historyInFlight.current = null;
    setHistoryState(emptyPracticeHistory(key, base));
  };
  useEffect(() => { setChanges({}); setAccepted(null); setSelectedId(null); resetHistory(null); }, [key]);
  useEffect(() => {
    setAccepted(null);
    resetHistory(source.data?.key === key ? source.data : null);
  }, [source.data, key]);
  const history = historyState.key === key && historyState.base === data
    ? historyState : emptyPracticeHistory(key, data);
  const allTasks = [...tasks, ...history.tasks];
  const currentHistoryScope = historyScope.current;
  async function loadMoreHistory() {
    if (!history.hasMore || !history.nextCursor || history.loading) return;
    const version = currentHistoryScope.version;
    if (historyInFlight.current?.key === key && historyInFlight.current.version === version) return;
    const sequence = ++historyRequestSequence.current;
    historyInFlight.current = { key, version, sequence };
    setHistoryState(current => {
      const base = current.key === key && current.base === data ? current : emptyPracticeHistory(key, data);
      return { ...base, loading: true, error: null };
    });
    const cursor = history.nextCursor;
    try {
      const path = target.domain === "private"
        ? `/admin/bookings/${target.bookingId}/practice-review/history`
        : "/school-domain/lesson-workspace/practice-review/history";
      const params = target.domain === "private" ? { cursor } : { sessionId: target.sessionId, pupilId: target.pupilId, cursor };
      const page = await get<LessonPracticeHistoryPage>(path, params);
      if (historyScope.current.key !== key || historyScope.current.version !== version || historyRequestSequence.current !== sequence) return;
      setHistoryState(current => {
        const base = current.key === key && current.base === data ? current : emptyPracticeHistory(key, data);
        const known = new Set([...tasks.filter(task => task.section === "completed"), ...base.tasks].map(task => task.id));
        const additions = page.tasks.filter(task => task.section === "completed" && !known.has(task.id));
        return { ...base, tasks: [...base.tasks, ...additions], nextCursor: page.nextCursor,
          hasMore: page.hasMore, error: null, loading: false };
      });
    } catch (cause) {
      if (historyScope.current.key !== key || historyScope.current.version !== version || historyRequestSequence.current !== sequence) return;
      setHistoryState(current => {
        const base = current.key === key && current.base === data ? current : emptyPracticeHistory(key, data);
        return { ...base, error: cause instanceof Error ? cause.message : "Could not load older completed tasks.", loading: false };
      });
    } finally {
      if (historyScope.current.key === key && historyScope.current.version === version && historyRequestSequence.current === sequence) {
        historyInFlight.current = null;
        setHistoryState(current => current.key === key && current.base === data ? { ...current, loading: false } : current);
      }
    }
  }
  return {
    tasks, loading: source.loading, error: source.error, awardsEnabled: data?.awardsEnabled === true,
    scopeKey: key,
    withdrawTarget: !target.accountId ? null : target.domain === "private"
      ? target.studentId ? { domain: "private" as const, studentId: target.studentId, bookingId: target.bookingId } : null
      : { domain: "school" as const, sessionId: target.sessionId, pupilId: target.pupilId },
    historyTasks: history.tasks, historyHasMore: history.hasMore, historyLoading: history.loading,
    historyError: history.error, loadMoreHistory,
    selected: allTasks.find(task => task.id === selectedId) ?? null,
    select: (id: string | null) => { if (id === null) opener.current?.focus(); setSelectedId(id); },
    open: (id: string, button: HTMLButtonElement) => { opener.current = button; setSelectedId(id); },
    change: (task: LessonPracticeTask, patch: Partial<PracticeReviewInput>) => {
      if (task.section === "completed") return;
      setChanges(current => ({ ...current, [task.id]: { ...task.review, ...current[task.id], ...patch, readyForReviewAt: task.readyForReviewAt } }));
      onChange?.();
    },
    getChanges: () => Object.values(changes),
    getConflictingDraftChanges: (values: PracticeReviewInput[]) => values.filter(value => {
      const task = (data?.tasks ?? []).find(candidate => candidate.id === value.todoId);
      return !task || task.review.version !== value.version || (task.readyForReviewAt ?? null) !== (value.readyForReviewAt ?? null);
    }),
    restoreChanges: (values: PracticeReviewInput[]) => {
      const restored = Object.fromEntries(values.flatMap(value => {
        const task = (data?.tasks ?? []).find(candidate => candidate.id === value.todoId);
        return task ? [[task.id, value] as const] : [];
      }));
      setChanges(restored);
      return values.filter(value => !(data?.tasks ?? []).some(task => task.id === value.todoId)).map(value => value.todoId);
    },
    accept: (value?: LessonPracticeReview) => {
      if (value) {
        const next = { ...value, key };
        setAccepted(next); setChanges({}); resetHistory(next);
      }
    },
    reload: () => { setAccepted(null); resetHistory(source.data?.key === key ? source.data : null); source.reload(); },
    discardAndReload: () => { setChanges({}); setAccepted(null); resetHistory(source.data?.key === key ? source.data : null); source.reload(); },
  };
}
export type PracticeReviewState = ReturnType<typeof useLessonPracticeReview>;

function useCompactReview() {
  const [compact, setCompact] = useState(() => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(max-width: 900px)").matches);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const query = window.matchMedia("(max-width: 900px)");
    const update = () => setCompact(query.matches);
    query.addEventListener("change", update); update();
    return () => query.removeEventListener("change", update);
  }, []);
  return compact;
}

export function CurrentLessonPractice({ state, busy }: { state: PracticeReviewState; busy: boolean }) {
  const current = state.tasks.filter(task => task.section === "previous");
  const added = state.tasks.filter(task => task.section === "new");
  return <section className="panel lesson-practice-current" aria-label="Practice to review">
    <div className="lesson-practice-heading"><h2>Practice to review</h2><span className="tag">{current.length} {current.length === 1 ? "task" : "tasks"}</span></div>
    <p className="small muted">From the previous lesson. Review the work, then keep it going or sign it off.</p>
    {state.loading && !state.tasks.length && <p className="small muted">Loading practice tasks…</p>}
    {state.error && <div className="error small">{state.error}<button type="button" className="quiet" onClick={state.reload}>Retry loading practice</button></div>}
    {!state.loading && !state.error && !current.length && <p className="small muted">No tasks from the previous lesson. Earlier open tasks are in Carried-over tasks.</p>}
    <div className="lesson-practice-task-list">{current.map(task => <TaskSummary key={task.id} task={task} state={state} busy={busy} />)}</div>
    {added.length > 0 && <details className="lesson-practice-added"><summary>Set in this lesson ({added.length})</summary>
      {added.map(task => <p className="small" key={task.id}><strong>{task.title}</strong><br /><span className="muted">For the next lesson</span></p>)}
    </details>}
  </section>;
}

export function LessonPracticeSide({ state, busy, children, allowWithdraw = false, onWithdrawBusyChange }: { state: PracticeReviewState; busy: boolean; children?: ReactNode; allowWithdraw?: boolean; onWithdrawBusyChange?: (busy: boolean) => void }) {
  const compact = useCompactReview();
  const older = state.tasks.filter(task => task.section === "carried");
  const completed = [...state.tasks.filter(task => task.section === "completed"), ...state.historyTasks];
  const [open, setOpen] = useState(!compact);
  useEffect(() => setOpen(!compact), [compact]);
  return <>
    <details className="panel lesson-practice-carried" open={open} onToggle={event => setOpen(event.currentTarget.open)}>
      <summary>Carried-over tasks <span className="tag">{older.length}</span></summary>
      <p className="small muted">Stay here until a tutor signs them off.</p>
      {!older.length && <p className="small muted">No older open tasks.</p>}
      <div className="lesson-practice-task-list">{older.map(task => <TaskSummary key={task.id} task={task} state={state} busy={busy} />)}</div>
    </details>
    {state.selected && (compact ? <Drawer title="Review practice task" eyebrow={state.selected.title} onClose={() => state.select(null)} busy={busy} size="medium">
      <ReviewEditor key={`${state.scopeKey}:${state.selected.id}`} task={state.selected} state={state} busy={busy} allowWithdraw={allowWithdraw} onWithdrawBusyChange={onWithdrawBusyChange} />
    </Drawer> : <section className="panel lesson-practice-review" aria-label="Selected practice task">
      <div className="lesson-practice-heading"><h2>Task review</h2><button type="button" className="quiet small" disabled={busy} onClick={() => state.select(null)}>Close</button></div>
      <ReviewEditor key={`${state.scopeKey}:${state.selected.id}`} task={state.selected} state={state} busy={busy} inline allowWithdraw={allowWithdraw} onWithdrawBusyChange={onWithdrawBusyChange} />
    </section>)}
    {children}
    <details className="panel lesson-practice-history"><summary>Completed tasks ({completed.length}{state.historyHasMore ? "+" : ""})</summary>
      {!completed.length && !state.historyHasMore && !state.historyError && <p className="small muted">Tasks you sign off will appear here.</p>}
      {completed.map(task => <TaskSummary key={task.id} task={task} state={state} busy={busy} />)}
      {state.historyHasMore && <p className="small muted">Showing {completed.length} completed tasks. Older tasks are available.</p>}
      {state.historyLoading && <p className="small muted" role="status">Loading older completed tasks…</p>}
      {state.historyError && <div className="error small" role="alert">{state.historyError}</div>}
      {state.historyHasMore && <button className="quiet small" type="button" disabled={state.historyLoading}
        onClick={() => { void state.loadMoreHistory(); }}>
        {state.historyLoading ? "Loading older tasks…" : state.historyError ? "Retry loading older tasks" : "Load older completed tasks"}
      </button>}
    </details>
    {(state.error || state.getChanges().length > 0) && <button className="quiet small" type="button" onClick={state.discardAndReload}>Refresh practice and discard unsaved reviews</button>}
  </>;
}

function TaskSummary({ task, state, busy }: { task: LessonPracticeTask; state: PracticeReviewState; busy: boolean }) {
  const touched = state.getChanges().some(change => change.todoId === task.id);
  const status = task.section === "completed" ? "Complete" : task.review.complete ? "Sign-off on publish" : task.readyForReviewAt ? "Ready for review" : "Keep practising";
  return <button type="button" className={`lesson-practice-task${state.selected?.id === task.id ? " is-selected" : ""}`} disabled={busy || state.loading} onClick={event => state.open(task.id, event.currentTarget)}>
    <span className="lesson-practice-task-title">{task.title}</span>
    <span className="small muted">Set {formatDate(task.sourceDate)} · {formatDuration(task.practiceSeconds)} logged</span>
    <span className="small lesson-practice-task-status">{status}{touched ? " · Unsaved review" : task.review.version > 0 && !task.completedAt ? " · Review saved" : ""}<span aria-hidden="true"> →</span></span>
  </button>;
}

function ReviewEditor({ task, state, busy, inline = false, allowWithdraw = false, onWithdrawBusyChange }: { task: LessonPracticeTask; state: PracticeReviewState; busy: boolean; inline?: boolean; allowWithdraw?: boolean; onWithdrawBusyChange?: (busy: boolean) => void }) {
  const id = useId();
  const heading = useRef<HTMLHeadingElement>(null);
  const [editingGuidance, setEditingGuidance] = useState(false);
  const [guidanceDescription, setGuidanceDescription] = useState("");
  const [guidanceMinutes, setGuidanceMinutes] = useState("");
  const [guidanceExpected, setGuidanceExpected] = useState<{ description: string | null; suggestedMinutes: number | null } | null>(null);
  const [guidanceSaving, setGuidanceSaving] = useState(false);
  const [guidanceProblem, setGuidanceProblem] = useState<string | null>(null);
  const guidanceLock = useRef(false);
  const guidanceRequest = useRef(false);
  const releaseGuidance = useRef<(() => void) | null>(null);
  useEffect(() => () => { if (guidanceLock.current) releaseGuidance.current?.(); }, []);
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawProblem, setWithdrawProblem] = useState<string | null>(null);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  // Inline desktop reviews should come into view when they receive focus.
  useEffect(() => { if (inline) heading.current?.focus(); }, [task.id, inline]);
  const closed = task.section === "completed";
  const target = state.withdrawTarget;
  const flagEnabled = target?.domain === "private"
    ? import.meta.env.VITE_PRACTICE_SAFE_WITHDRAW_ENABLED === "true"
    : import.meta.env.VITE_SCHOOL_PRACTICE_SAFE_WITHDRAW_ENABLED === "true";
  const canWithdraw = flagEnabled && target !== null && !closed;
  const blockedWithdraw = busy || !allowWithdraw || state.loading || state.getChanges().length > 0;
  const guidanceFlag = target?.domain === "private"
    ? import.meta.env.VITE_PRACTICE_GUIDANCE_EDIT_ENABLED === "true"
    : import.meta.env.VITE_SCHOOL_PRACTICE_GUIDANCE_EDIT_ENABLED === "true";
  const canEditGuidance = guidanceFlag && target !== null && !closed;
  function beginGuidance() {
    if (blockedWithdraw || !target || !guidanceFlag || guidanceLock.current) return;
    setGuidanceExpected({ description: task.description ?? null, suggestedMinutes: task.suggestedMinutes ?? null });
    setGuidanceDescription(task.description ?? "");
    setGuidanceMinutes(task.suggestedMinutes == null ? "" : String(task.suggestedMinutes));
    setGuidanceProblem(null);
    guidanceLock.current = true;
    releaseGuidance.current = () => onWithdrawBusyChange?.(false);
    onWithdrawBusyChange?.(true);
    setEditingGuidance(true);
  }
  function endGuidance(refresh: boolean) {
    guidanceLock.current = false;
    setEditingGuidance(false);
    releaseGuidance.current?.();
    releaseGuidance.current = null;
    if (refresh) state.reload();
  }
  async function saveGuidance() {
    if (!target || !guidanceExpected || !guidanceLock.current || guidanceRequest.current) return;
    const minutes = guidanceMinutes.trim() === "" ? null : Number(guidanceMinutes);
    if (minutes !== null && (!Number.isInteger(minutes) || minutes < 1 || minutes > 480)) {
      setGuidanceProblem("Enter a whole number from 1 to 480, or leave minutes blank."); return;
    }
    const guidance = { description: guidanceDescription === "" ? null : guidanceDescription, suggestedMinutes: minutes };
    guidanceRequest.current = true;
    setGuidanceSaving(true);
    setGuidanceProblem(null);
    try {
      if (target.domain === "private") {
        await send("PATCH", `/admin/bookings/${encodeURIComponent(target.bookingId)}/practice-todos/${encodeURIComponent(task.id)}/guidance`,
          { expected: guidanceExpected, guidance });
      } else {
        await send("POST", "/school-domain/lesson-workspace/practice/guidance", {
          sessionId: target.sessionId, pupilId: target.pupilId, todoId: task.id, expected: guidanceExpected, guidance,
        });
      }
      if (!mounted.current) return;
      endGuidance(false);
      state.select(null);
      state.reload();
    } catch {
      if (mounted.current) setGuidanceProblem("The change could not be confirmed. Retry, or cancel and refresh the task to check the current guidance.");
    } finally {
      guidanceRequest.current = false;
      if (mounted.current) setGuidanceSaving(false);
    }
  }
  async function withdraw() {
    if (inFlight.current || blockedWithdraw || !target || !flagEnabled) return;
    inFlight.current = true;
    setWithdrawing(true);
    onWithdrawBusyChange?.(true);
    setWithdrawProblem(null);
    try {
      if (target.domain === "private") {
        await send("POST", `/admin/bookings/${encodeURIComponent(target.bookingId)}/practice-todos/${encodeURIComponent(task.id)}/withdraw`);
      } else {
        await send("POST", "/school-domain/lesson-workspace/practice/withdraw", {
          sessionId: target.sessionId, pupilId: target.pupilId, todoId: task.id,
        });
      }
      if (!mounted.current) return;
      state.select(null);
      state.reload();
    } catch {
      if (mounted.current) setWithdrawProblem("The withdrawal could not be confirmed. Retry this same request or close and refresh the task before continuing.");
    } finally {
      inFlight.current = false;
      onWithdrawBusyChange?.(false);
      if (mounted.current) setWithdrawing(false);
    }
  }
  return <div className="lesson-practice-review-fields">
    <h3 ref={heading} tabIndex={-1}>{task.title}</h3>
    <p className="small muted">Set {formatDate(task.sourceDate)} · {formatDuration(task.practiceSeconds)} logged{task.suggestedMinutes ? ` · Aim for ${task.suggestedMinutes} min/day` : ""}</p>
    {task.description && <p className="small lesson-practice-instructions">{task.description}</p>}
    {canEditGuidance && !editingGuidance && <button type="button" className="quiet small"
      disabled={blockedWithdraw || withdrawing} onClick={beginGuidance}>Edit practice guidance</button>}
    {editingGuidance && <section aria-label="Edit practice guidance">
      <p className="small muted">The learner can see these instructions and suggested minutes.</p>
      <label htmlFor={`${id}-guidance`}>Practice instructions</label>
      <textarea id={`${id}-guidance`} rows={4} maxLength={target?.domain === "private" ? 2000 : 4000}
        value={guidanceDescription} disabled={guidanceSaving} onChange={event => setGuidanceDescription(event.target.value)} />
      <label htmlFor={`${id}-minutes`}>Suggested minutes per day</label>
      <input id={`${id}-minutes`} type="number" min="1" max="480" step="1" value={guidanceMinutes}
        disabled={guidanceSaving} onChange={event => setGuidanceMinutes(event.target.value)} />
      {guidanceProblem && <p role="alert" className="error small">{guidanceProblem}</p>}
      <button type="button" disabled={guidanceSaving} onClick={() => void saveGuidance()}>{guidanceSaving ? "Saving…" : "Save guidance"}</button>
      <button type="button" className="quiet" disabled={guidanceSaving} onClick={() => endGuidance(Boolean(guidanceProblem))}>Cancel</button>
    </section>}
    <PracticeTaskMaterials materials={task.materials} />
    {task.lastReview && <p className="small muted">Last review: effort {task.lastReview.effortRating == null ? "not assessed" : `${task.lastReview.effortRating}/5`} · progress {task.lastReview.progressRating == null ? "not assessed" : `${task.lastReview.progressRating}/5`}</p>}
    {closed ? <p className="small">Completed{task.completedAt ? ` ${formatDate(task.completedAt)}` : ""}.</p> : <>
      <div className="lesson-practice-private"><span aria-hidden="true">🔒 </span>Tutor only — these scores are hidden from students and parents.</div>
      {(task.review.readyForReviewAt ?? null) !== task.readyForReviewAt && <div role="status">
        <p className="small">The family's review request changed since this draft. Check the task before publishing.</p>
        <button type="button" className="quiet small" disabled={busy} onClick={() => state.change(task, {})}>Confirm review request</button>
      </div>}
      <Rating label="Effort" value={task.review.effortRating} disabled={busy} onChange={effortRating => state.change(task, { effortRating })} />
      <Rating label="Progress" value={task.review.progressRating} disabled={busy} onChange={progressRating => state.change(task, { progressRating })} />
      <label htmlFor={`${id}-feedback`}>Task feedback</label>
      <p className="small muted">Added as a comment on this practice task when you publish the lesson.</p>
      <textarea id={`${id}-feedback`} value={task.review.comment} maxLength={2000} rows={4} disabled={busy} onChange={event => state.change(task, { comment: event.target.value })} placeholder="What went well, and what to work on next…" />
      {state.awardsEnabled ? <fieldset className="lesson-practice-outcome" disabled={busy}><legend>Tutor confirmation</legend>
        <label><input type="checkbox" checked={task.review.complete} onChange={event => state.change(task, { complete: event.target.checked })}/> Tutor confirmed complete</label>
        <p className="small muted">Publish the lesson to sign off this task and award Practice Legend. Each task counts once towards the 10, 25, 50, 75, 100, 150 and 200 task medals.</p>
      </fieldset> : <fieldset className="lesson-practice-outcome" disabled={busy}><legend>After this lesson</legend>
        <label><input type="radio" name={`${id}-outcome`} checked={!task.review.complete} onChange={() => state.change(task, { complete: false })} /> Keep practising</label>
        <label><input type="radio" name={`${id}-outcome`} checked={task.review.complete} onChange={() => state.change(task, { complete: true })} /> Mark complete</label>
      </fieldset>}
      {state.awardsEnabled && <fieldset className="award-tutor-recognitions" disabled={busy}><legend>Recognise an achievement</legend>
        {TUTOR_RECOGNITIONS.map(key => {
          const definition = AWARD_CATALOGUE.find(award => award.key === key)!;
          return <label key={key} className="award-tutor-recognition"><input type="checkbox" aria-labelledby={`${id}-${key}-title`} aria-describedby={`${id}-${key}-description`} checked={task.review.recognitions?.includes(key) ?? false} onChange={event => {
            const current = task.review.recognitions ?? [];
            state.change(task, { recognitions: event.target.checked ? [...current, key] : current.filter(value => value !== key) });
          }}/><span><strong id={`${id}-${key}-title`}>{definition.title}</strong><span className="small muted" id={`${id}-${key}-description`}>{definition.description}</span></span></label>;
        })}
        <p className="small muted">These medals are awarded when you publish, alongside any task feedback.</p>
      </fieldset>}
      <p className="small muted">Save draft to keep the review private. Publish to share feedback and apply sign-off.</p>
    </>}
    {canWithdraw && <>
      <button type="button" className="quiet small" disabled={blockedWithdraw || withdrawing} onClick={() => setConfirmWithdraw(true)}>Withdraw task</button>
      {blockedWithdraw && !busy && <p className="small muted">Save or discard lesson and task review changes before withdrawing.</p>}
      {confirmWithdraw && <ConfirmDialog title="Withdraw this practice task?" description={task.title}
        confirmLabel="Withdraw task" confirmingLabel="Withdrawing…" busy={withdrawing}
        onClose={() => { setConfirmWithdraw(false); if (withdrawProblem) state.reload(); }}
        onConfirm={() => void withdraw()}>
        <p>The assignment will leave current practice. Its history and materials remain recorded.</p>
        {withdrawProblem && <p className="error small" role="alert">{withdrawProblem}</p>}
      </ConfirmDialog>}
    </>}
    {task.comments.length > 0 && <details><summary className="small">Recent published task feedback ({task.comments.length})</summary>
      {task.comments.map((comment, index) => <p className="small lesson-practice-instructions" key={index}><span className="muted">{comment.author} · {formatDate(comment.publishedAt)}</span><br />{comment.comment}</p>)}
    </details>}
  </div>;
}
function Rating({ label, value, disabled, onChange }: { label: string; value: number | null; disabled: boolean; onChange: (value: number | null) => void }) {
  return <fieldset className="lesson-practice-rating" disabled={disabled}><legend>{label}</legend><div>
    {[1,2,3,4,5].map(score => <button type="button" key={score} className={value === score ? "is-selected" : "quiet"} aria-label={`${label} ${score} out of 5`} aria-pressed={value === score} onClick={() => onChange(score)}>{score}</button>)}
  </div><button type="button" className="quiet small" aria-pressed={value === null} onClick={() => onChange(null)}>{value === null ? "Not assessed" : "Clear score"}</button></fieldset>;
}

export function CreateLessonPracticeTask({ domain, studentId, sessionId, pupilId, pupilName, ensureNote, draft, onDraftChange, onChooseGroove, onClose, onSaved }: {
  domain: "private" | "school"; studentId?: string; sessionId?: string; pupilId?: string; pupilName: string;
  draft?: PracticeTaskDraft; onDraftChange?: (draft: PracticeTaskDraft) => void;
  ensureNote?: () => Promise<string | null>; onChooseGroove?: () => void; onClose: () => void; onSaved: () => void;
}) {
  const [localDraft, setLocalDraft] = useState(emptyPracticeTaskDraft);
  const currentDraft = draft ?? localDraft;
  const { title, description, suggestedMinutes: minutes } = currentDraft;
  const changeDraft = onDraftChange ?? setLocalDraft;
  const [busy, setBusy] = useState(false); const [error, setError] = useState<string | null>(null);
  // Keep the same key for a retry of the same payload, including an uncertain response.
  const requestKey = useRef<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const id = useId();
  async function create() {
    setBusy(true); setError(null); setSubmitted(true);
    try {
      const key = requestKey.current ??= practiceRequestKey();
      const input = { title: title.trim(), description: description.trim() || null, suggestedMinutes: minutes ? Number(minutes) : null };
      if (domain === "private") {
        const sourceLessonId = await ensureNote?.();
        if (!sourceLessonId) throw new Error("Save the lesson draft before setting this task.");
        await addStaffPracticeTodo(studentId!, { ...input, sourceLessonId, requestId: key });
      } else await send("POST", "/school-domain/lesson-workspace/practice", { ...input, sessionId, pupilId }, { "Idempotency-Key": key });
      onSaved(); onClose();
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Could not add practice task."); }
    finally { setBusy(false); }
  }
  return <Drawer title="Practice task" eyebrow={pupilName} description="Set work for the next lesson. It carries over until a tutor signs it off." onClose={onClose} busy={busy} size="medium">
    {onChooseGroove && <PracticeTaskGrooveChoice disabled={busy || submitted} onChoose={onChooseGroove} />}
    <form className="stack" onSubmit={event => { event.preventDefault(); void create(); }}>
      <label htmlFor={`${id}-title`}>Task</label><input id={`${id}-title`} required maxLength={200} value={title} disabled={busy || submitted} onChange={event => changeDraft({ ...currentDraft, title: event.target.value })} />
      <label htmlFor={`${id}-instructions`}>Instructions</label><textarea id={`${id}-instructions`} maxLength={2000} rows={4} value={description} disabled={busy || submitted} onChange={event => changeDraft({ ...currentDraft, description: event.target.value })} />
      <label htmlFor={`${id}-minutes`}>Suggested minutes per day (optional)</label><input id={`${id}-minutes`} type="number" min={1} max={480} value={minutes} disabled={busy || submitted} onChange={event => changeDraft({ ...currentDraft, suggestedMinutes: event.target.value })} />
      {error && <div className="error small">{error}</div>}
      <button disabled={busy || !title.trim()} type="submit">{busy ? "Adding…" : submitted ? "Retry adding task" : "Add practice task"}</button>
    </form>
  </Drawer>;
}


function practiceRequestKey(): string {
  const crypto = globalThis.crypto;
  if (typeof crypto?.randomUUID === "function") return crypto.randomUUID();
  // Older tablet browsers expose secure random bytes but not randomUUID.
  if (typeof crypto?.getRandomValues !== "function") throw new Error("This browser cannot save a practice task. Please try a current browser.");
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 15) | 64;
  bytes[8] = (bytes[8]! & 63) | 128;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20)}`;
}
